// Date-range filtering for the Bills list (job 75, "Add Dates Filter").
//
// This lives outside BillsListPage for the same reason parseState does: the two
// non-obvious decisions here — that a period filter is an OVERLAP test, and that
// `created_at` must be read as a LOCAL date — are the kind that look arbitrary
// six months later and silently return the wrong rows if changed. In a page
// component they are untestable (vitest runs `environment: 'node'`, so importing
// a .tsx pulls in React and fails). Here they are covered by billDateFilter.test.ts.

import type { BvBill } from '../types/billTypes';

// The Bills list shows two different dates and they answer different questions,
// so the filter states which one it is narrowing rather than choosing for the
// user:
//   period  — when the electricity was used (the Period column). What "show me
//             the June bills" means.
//   created — when the row was added here (the Created column). What "what did
//             we load last week" means.
export type DateBasis = 'period' | 'created';

export const DATE_BASIS_LABEL: Record<DateBasis, string> = {
  period: 'Bill period',
  created: 'Created',
};

/**
 * An ISO timestamp as its LOCAL calendar date, `YYYY-MM-DD`.
 *
 * Not `created_at.slice(0, 10)`, which is the UTC date. A bill created
 * 2026-05-31T23:00Z is 9am on 1 June in Melbourne: the Created column renders it
 * as 1/6, so a "from 1 June" filter reading the UTC date would hide a row the
 * table says is in range. Same discipline as billStatus in BillsListPage — the
 * column and the filter that narrows it have to agree on what a row *is*.
 */
export function localDateKey(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Whether a bill falls in [from, to] on the chosen basis. Either bound may be
 * empty, which leaves that end open; both empty matches everything.
 *
 * `bill_from` / `bill_to` are Postgres DATE columns and arrive as `YYYY-MM-DD`,
 * so they compare correctly as plain strings — no Date parsing, no timezone.
 */
export function inDateRange(
  bill: Pick<BvBill, 'bill_from' | 'bill_to' | 'created_at'>,
  basis: DateBasis,
  from: string,
  to: string,
): boolean {
  if (!from && !to) return true;

  // An inverted range is user error, not a query, and the page says so. Without
  // this guard the period branch below does NOT return nothing — both halves of
  // the overlap test pass, so [30 Jun, 1 Jun] quietly answers a different
  // question: every bill whose period covers BOTH dates. Refusing it outright
  // is the only reading that matches the warning shown on screen.
  if (from && to && from > to) return false;

  if (basis === 'created') {
    const key = localDateKey(bill.created_at);
    if (!key) return false;
    return (!from || key >= from) && (!to || key <= to);
  }

  // A bill period is a RANGE, so this is an overlap test, not containment:
  // asking for 1–30 June must return a bill running 15 May – 14 June, which is
  // a June bill that a containment test would silently drop. Both ends inclusive.
  const start = bill.bill_from ?? '';
  const end = bill.bill_to ?? '';
  if (!start || !end) return false;
  if (from && end < from) return false;
  if (to && start > to) return false;
  return true;
}
